import { useRef } from 'react'

import { motion, useInView } from 'framer-motion'

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: [0.25, 0, 0.1, 1], delay }}
    >
      {children}
    </motion.div>
  )
}

export default function PropertyRules() {
  return (
    <div className="min-h-screen bg-cream-100">

      {/* Hero with image */}
      <div className="relative h-72 lg:h-96 overflow-hidden">
        <img
          src="/images/optimized/IMG_5109.webp"
          fetchPriority="high"
          className="h-full w-full object-cover"
          alt="Forest at Scenic Solitude"
        />
        <div className="absolute inset-0 bg-forest-950/55" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h1 className="text-4xl lg:text-5xl font-semibold text-white tracking-tight">Guidelines for a Gentle Stay</h1>
          <p className="mt-3 text-sm text-white/60 max-w-md">To protect the peace of the valley and ensure your safety in the mountains.</p>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-2xl px-8 lg:px-0 py-20 space-y-14">

        <FadeIn>
          <p className="text-[10px] tracking-[0.35em] uppercase text-ink-light font-semibold mb-4">House Guidelines.</p>
          <p className="text-sm text-ink-soft leading-[1.95]">
            We ask all our guests to live by a few simple rhythms during their stay. These are not rules for rules' sake. Each one exists to protect the land, the silence, and the experience that brought you here.
          </p>
        </FadeIn>

        {/* 01 */}
        <FadeIn delay={0.05}>
          <div className="flex items-baseline gap-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-light font-semibold">01</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">Arrival &amp; Departure</h2>
          </div>
          <div className="text-sm text-ink-soft leading-[1.95] space-y-4">
            <p>
              Check-in is between <strong className="text-ink font-medium">2:00 PM and 6:00 PM</strong>. We strongly encourage you to arrive before sunset. Mountain roads and forest trails are beautiful but can be difficult to navigate in the dark. Arriving with the daylight means you settle in properly, with time to breathe.
            </p>
            <p>
              We say goodbye at <strong className="text-ink font-medium">11:00 AM</strong>. Because our rooms are crafted with natural wood and stone, they require deep, careful cleaning before the next guest arrives. We hope your mornings here are slow enough that 11:00 comes later than it usually does.
            </p>
          </div>
        </FadeIn>

        {/* 02 */}
        <FadeIn delay={0.08}>
          <div className="flex items-baseline gap-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-light font-semibold">02</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">The Gift of Silence</h2>
          </div>
          <div className="text-sm text-ink-soft leading-[1.95] space-y-4">
            <p>
              When the sun goes down, the forest takes over. After <strong className="text-ink font-medium">9:00 PM</strong>, we ask that all guests respect the natural quiet of the mountains. The sounds you will hear instead are ones you may not find easily again: wind through the oaks, the distant call of a nightbird.
            </p>
            <p>
              Playing music through personal speakers is not permitted in open areas or balconies at any time. This is not about silence for its own sake. It is about protecting something rare. We invite you to listen to what is already here.
            </p>
          </div>
        </FadeIn>

        {/* 03 */}
        <FadeIn delay={0.1}>
          <div className="flex items-baseline gap-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-light font-semibold">03</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">Walking Softly on the Land</h2>
          </div>
          <div className="text-sm text-ink-soft leading-[1.95] space-y-4">
            <p>
              Our treehouses and rooms are built on hand-laid local wooden floors, crafted with time and care. We ask that you leave your outdoor shoes at the entrance to your room. It keeps the wood clean and, in a small way, marks the transition from the mountain outside to the quieter world within.
            </p>
            <p>
              Scenic Solitude is a <strong className="text-ink font-medium">plastic-free property</strong>. Please do not leave food wrappers, packaging, or trash on your balcony or along the forest trails. The mountain wildlife roams freely at night, and we want to keep them safe. What you bring up, we ask that you bring back down.
            </p>
          </div>
        </FadeIn>

        {/* 04 */}
        <FadeIn delay={0.12}>
          <div className="flex items-baseline gap-4 mb-5">
            <span className="text-[10px] tracking-[0.3em] uppercase text-ink-light font-semibold">04</span>
            <h2 className="text-xl font-semibold text-ink tracking-tight">Mountain Dining</h2>
          </div>
          <div className="text-sm text-ink-soft leading-[1.95] space-y-4">
            <p>
              Our small kitchen prepares fresh meals daily using ingredients sourced from within the valley. Because of this, we cook at set times and cannot accommodate orders outside the kitchen hours. Each morning we will share the day's menu with you.
            </p>
            <p>
              If you have any food allergies or dietary needs, please let us know before you arrive, not on the day. It allows us to plan with care and make sure your meals here are exactly as they should be.
            </p>
          </div>
        </FadeIn>

        {/* Closing */}
        <FadeIn delay={0.15} className="border-t border-cream-300 pt-12">
          <p className="text-sm text-ink-soft leading-[1.95] italic">
            These guidelines exist because we believe that a truly restful stay is one that leaves the mountain exactly as you found it, or perhaps a little more loved. Thank you for being here with care.
          </p>
        </FadeIn>

      </div>
    </div>
  )
}
